import { useState } from "react";
import { useLandingEdit } from "./LandingEditContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Settings2 } from "lucide-react";

interface ButtonData {
  text: string;
  url: string;
}

interface EditableButtonProps {
  contentKey: string;
  className?: string;
  variant?: "default" | "outline" | "secondary" | "ghost" | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  defaultText?: string;
  defaultUrl?: string;
  children?: React.ReactNode;
}

function parseButtonData(content: string, defaultText: string, defaultUrl: string): ButtonData {
  if (!content) {
    return { text: defaultText, url: defaultUrl };
  }
  try {
    const parsed = JSON.parse(content);
    return {
      text: parsed.text || defaultText,
      url: parsed.url || defaultUrl,
    };
  } catch {
    return { text: content || defaultText, url: defaultUrl };
  }
}

export function EditableButton({
  contentKey,
  className,
  variant = "default",
  size = "default",
  defaultText = "Click here",
  defaultUrl = "#",
  children,
}: EditableButtonProps) {
  const { isEditMode, getContent, updateLocalContent } = useLandingEdit();
  const currentValue = getContent(contentKey);
  const buttonData = parseButtonData(currentValue, defaultText, defaultUrl);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editText, setEditText] = useState(buttonData.text);
  const [editUrl, setEditUrl] = useState(buttonData.url);

  const handleSave = () => {
    updateLocalContent(contentKey, JSON.stringify({ text: editText, url: editUrl }));
    setPopoverOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setEditText(buttonData.text);
      setEditUrl(buttonData.url);
    }
    setPopoverOpen(open);
  };

  if (!isEditMode) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        asChild={!!buttonData.url && buttonData.url !== "#"}
        data-testid={`button-${contentKey}`}
      >
        {buttonData.url && buttonData.url !== "#" ? (
          <a href={buttonData.url}>{children || buttonData.text}</a>
        ) : (
          <span>{children || buttonData.text}</span>
        )}
      </Button>
    );
  }

  return (
    <div className="relative inline-flex items-center gap-1">
      <Button
        variant={variant}
        size={size}
        type="button"
        className={cn(
          className,
          "ring-2 ring-primary/50 ring-offset-2 ring-offset-background"
        )}
        data-testid={`editable-button-${contentKey}`}
      >
        {children || buttonData.text}
      </Button>
      <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8 absolute -right-10 top-1/2 -translate-y-1/2"
            type="button"
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`${contentKey}-text`}>Button Text</Label>
              <Input
                id={`${contentKey}-text`}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Button text..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${contentKey}-url`}>Link URL</Label>
              <Input
                id={`${contentKey}-url`}
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <Button onClick={handleSave} className="w-full" type="button">
              Apply Changes
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
